/**
 * English message catalogue.
 *
 * docs/08-frontend-and-ux.md §1.2 and §11: no hardcoded strings in components —
 * every visible string is a key from day one, so M6 (docs/12) only has to add a
 * second locale and the RTL pass, not go hunting through JSX.
 */

export const en = {
  translation: {
    app: {
      name: 'FamTree',
      tagline: 'A private family record',
      loading: 'Loading…',
      retry: 'Retry',
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
      yes: 'Yes',
      no: 'No',
    },
    nav: {
      home: 'Home',
      tree: 'Tree',
      people: 'People',
      photos: 'Photos',
      kinship: 'Kinship',
      proposals: 'Proposals',
      admin: 'Admin',
      council: 'Council',
      audit: 'Audit log',
      adminSettings: 'Settings',
      notifications: 'Notifications',
      signOut: 'Sign out',
      skipToContent: 'Skip to content',
    },
    login: {
      title: 'Sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      inviteOnly: 'Access is by invitation from a family member. There is no self-registration.',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },
    invite: {
      title: 'Welcome to the family tree',
      invitedBy: 'You were invited by {name}',
      forEmail: 'This invitation is for {email}.',
      displayName: 'Display name',
      password: 'Password',
      confirm: 'Confirm password',
      submit: 'Create my account',
      submitting: 'Creating…',
      mismatch: 'The two passwords do not match.',
      expired: 'This invitation is no longer valid. Ask the person who invited you for a new one.',
    },
    reset: {
      title: 'Choose a new password',
      password: 'New password',
      confirm: 'Confirm new password',
      submit: 'Set password',
      missingToken: 'This link is incomplete. Ask an administrator for a new one.',
    },
    home: {
      greeting: 'Welcome, {name}',
      milestone:
        'The account and governance foundations are in place. Persons, relations, kinship, photos and the tree views arrive in the next milestones.',
      yourAccount: 'Your account',
      role: 'Role',
      status: 'Status',
      seat: 'Council seat',
      noSeat: 'None',
      canPropose: 'May open proposals',
      yes: 'Yes',
      no: 'No',
      linkedPerson: 'Linked person',
      notLinked: 'Not linked yet',
      notLinkedHelp:
        'An administrator links your account to your person in the tree. Until then you can browse, but not propose changes.',
      council: 'Council',
      councilSeats: '{active} of {size} seats granted',
      threshold: 'Approval threshold: {threshold}',
      bootstrapTitle: 'Bootstrap mode is active',
      bootstrapBody:
        'The council is not yet quorate, so an administrator enters founding data directly. Once {threshold} seats are granted, every change goes to a vote — permanently.',
    },
    fuzzyDate: {
      year: 'Year',
      month: 'Month',
      day: 'Day',
      qualifier: 'Qualifier',
      none: '—',
      asWritten: 'As written',
      asWrittenPlaceholder: 'e.g. “spring 1943”, “before the war”',
      preview: 'Preview',
      unknown: 'Date unknown',
      qualifiers: {
        exact: 'exact',
        about: 'about (~)',
        before: 'before (<)',
        after: 'after (>)',
        estimated: 'estimated (est.)',
      },
      problems: {
        month_requires_year: 'A month needs a year.',
        day_requires_month: 'A day needs a month.',
        qualifier_requires_year: 'A qualifier needs a year.',
        year_out_of_range: 'That year is out of range.',
        month_out_of_range: 'That month is out of range.',
        day_out_of_range: 'That day is out of range.',
        day_not_in_month: 'That month does not have that many days.',
        unknown_qualifier: 'That qualifier is not one we record.',
      },
    },
    enums: {
      assertion_status: {
        pending: 'Pending',
        validated: 'Validated',
        rejected: 'Rejected',
        retired: 'Retired',
      },
      gender: { female: 'Female', male: 'Male', other: 'Other', unknown: 'Unknown' },
      living_status: { living: 'Living', deceased: 'Deceased', unknown: 'Unknown' },
      relation_kind: { parent_child: 'Parent–child', union: 'Union', sibling: 'Sibling' },
      parent_role: { mother: 'Mother', father: 'Father', parent: 'Parent' },
      parent_basis: {
        biological: 'biological',
        adoptive: 'adoptive',
        step: 'step',
        foster: 'foster',
        guardianship: 'guardianship',
        unknown: 'basis unknown',
      },
      union_type: {
        marriage: 'Marriage',
        civil_union: 'Civil union',
        partnership: 'Partnership',
        unknown: 'Union',
      },
      union_end_reason: {
        divorce: 'divorce',
        annulment: 'annulment',
        separation: 'separation',
        widowhood: 'widowhood',
        dissolved: 'dissolved',
        unknown: 'ended',
      },
      sibling_basis: {
        full: 'Full',
        half: 'Half',
        step: 'Step',
        adoptive: 'Adoptive',
        unknown: 'Unknown',
      },
      retire_reason: {
        error: 'Recorded in error',
        superseded: 'Superseded by another entry',
        privacy: 'Privacy',
        duplicate: 'Duplicate of another entry',
      },
      proposal_kind: {
        add_person: 'Add person',
        edit_person: 'Edit person',
        retire_person: 'Retire person',
        add_relation: 'Add relation',
        edit_relation: 'Edit relation',
        retire_relation: 'Retire relation',
      },
      proposal_status: {
        open: 'Open',
        approved: 'Approved',
        rejected: 'Rejected',
        expired: 'Expired',
        withdrawn: 'Withdrawn',
      },
      vote_choice: { approve: 'Approve', reject: 'Reject', abstain: 'Abstain' },
    },
    people: {
      title: 'People',
      search: 'Search',
      searchPlaceholder: 'Name, maiden name or nickname',
      sort: 'Sort',
      sorts: {
        default: 'Best match / family name',
        family_name: 'Family name',
        birth: 'Born, earliest first',
        '-birth': 'Born, latest first',
        '-created_at': 'Recently added',
      },
      count: '{count, plural, one {# person} other {# people}}',
      empty: 'Nobody has been recorded yet.',
      noMatches: 'Nobody matches “{q}”.',
      moreAvailable: 'More results are available — narrow the search to see them.',
      addPerson: 'Add a person',
      addRelation: 'Add a relation',
      editDetails: 'Edit details',
      retire: 'Retire…',
      retireExplain:
        'Retiring keeps the record and its history; it only removes the person from the live tree.',
      retireReason: 'Reason',
      confirmRetire: 'Retire this person',
      change: 'Change',
      thisPerson: 'this person',
      sibling: 'sibling',
      tabs: { family: 'Family', photos: 'Photos', biography: 'Biography', history: 'History 🔒' },
      groups: {
        unions: 'Unions',
        parents: 'Parents',
        children: 'Children',
        siblings: 'Siblings (recorded)',
        allAssertions: 'All assertions, every status',
        audit: 'Audit trail',
      },
      explicitSiblingsOnly:
        'Only siblings recorded explicitly. Where both shared parents are known, siblinghood is derived instead.',
      howRelated: 'How are we related?',
      showInTree: 'Show in the tree',
      nameOptional:
        'Every name here is optional. If the family cannot name this person — a great-grandmother nobody recorded — leave them blank; the tree will describe them by their place in it instead, such as “mother of Hassan”.',
      duplicateTitle:
        '{count, plural, one {A person already recorded looks similar} other {# people already recorded look similar}}',
      duplicateHelp:
        'Check before adding — a duplicate is much harder to undo than to avoid. If this is somebody else, carry on.',
      duplicateDismiss: 'Not a duplicate',
      draftRestored: 'An unfinished draft was restored from this browser.',
      draftDiscard: 'Start fresh',
      addChild: 'Add a child',
      addChildOfCouple: 'Add a child of this couple',
      addSibling: 'Add a sibling',
      addParent: 'Add a parent',
      entryHelpers: 'Keep going from here:',
      pendingProposals:
        '{count, plural, one {# open proposal concerns} other {# open proposals concern}} this person.',
      noBiography: 'No biography has been written yet.',
      sourceNote: 'Source: {note}',
      noAudit: 'Nothing has been recorded against this person yet.',
      newTitle: 'Add a person',
      editTitle: 'Edit person',
      backToList: 'Back to the list',
      cannotProposeTitle: 'You cannot change the tree yet',
      cannotProposeBody:
        'Changes are made by administrators while the family is being entered, and by council proposal afterwards. Ask an administrator to link your account to your person in the tree.',
      sections: {
        identity: '1 · Identity',
        connections: '2 · How are they connected?',
        evidence: '3 · Evidence & summary',
      },
      connectionsHelp:
        'Each connection is recorded as its own assertion, so it can be corrected — or, once the council is voting, approved — on its own.',
      connectionOther: 'Connected to',
      addConnection: '+ Add a connection',
      removeConnection: 'Remove this connection',
      direction: 'Which way round?',
      directionIsParent: '{name} is the parent',
      directionIsChild: '{name} is the child',
      fields: {
        given_names: 'Given names',
        family_name: 'Family name',
        birth_family_name: 'Name at birth',
        nickname: 'Nickname',
        gender: 'Gender',
        living_status: 'Living status',
        birth: 'Birth',
        death: 'Death',
        birth_place: 'Birthplace',
        death_place: 'Place of death',
        bio_note: 'Biography',
        source_note: 'Source note',
        summary: 'Summary',
      },
      problems: {
        death_requires_deceased: 'A death date belongs only to someone recorded as deceased.',
        death_before_birth: 'A death cannot come before a birth.',
      },
    },
    relations: {
      newTitle: 'Record a relation',
      kind: 'Kind',
      parent: 'Parent',
      child: 'Child',
      partnerA: 'Partner',
      partnerB: 'Partner',
      siblingA: 'Sibling',
      siblingB: 'Sibling',
      parentRole: 'Role',
      parentBasis: 'Basis',
      unionType: 'Union type',
      started: 'Started',
      ended: 'Ended',
      endReason: 'How it ended',
      ongoing: 'Ongoing',
      endReasonRequired: 'An end date needs a reason.',
      siblingBasis: 'Basis',
      note: 'Note',
      sourceNote: 'Source note',
      siblingFallback:
        'Record siblings directly only when the shared parents are not in the tree. When both parents are recorded, siblinghood is derived and this row would duplicate it.',
      basisHelp: {
        biological: 'Birth parent.',
        adoptive: 'Legal adoption — traversed for kinship, and labelled as adoptive.',
        step: 'A parent’s partner acting as a parent, without adoption. Not traversed as ancestry; step relations are derived from the union instead.',
        foster: 'Foster care — traversed for kinship and annotated.',
        guardianship: 'Legal guardian — traversed, annotated, and usually time-bounded.',
        unknown: 'Basis unrecorded — traversed without annotation.',
      },
    },
    admin: {
      settingsTitle: 'Council settings',
      councilSize: 'Council size',
      approvalThreshold: 'Approval threshold',
      votingWindow: 'Voting window',
      activeSeats: 'Seats granted',
      bootstrapMode: 'Bootstrap mode',
      graphVersion: 'Graph version',
      days: '{count, plural, one {# day} other {# days}}',
      bootstrapExplain:
        '{granted} of {threshold} seats are granted. When the {threshold}th is granted, bootstrap mode ends permanently and every change goes to a vote.',
      editorOnCouncil: 'These values are edited alongside the seats they govern:',
    },
    proposals: {
      title: 'Proposals',
      tabs: {
        needs_my_vote: 'Awaiting my vote',
        open: 'Open',
        mine: 'Mine',
        closed: 'Closed',
      },
      empty: {
        needs_my_vote: 'Nothing is waiting on your ballot.',
        open: 'Nothing is open. Propose a change from any profile.',
        mine: 'You have not proposed anything yet.',
        closed: 'No proposal has been decided yet.',
      },
      tallyLine:
        '{approve}/{threshold} approve · {reject} reject · {abstain} abstain · {notVoted} not voted',
      fact: {
        needed: '{count, plural, one {# more approval needed} other {# more approvals needed}}',
        decidingNow: 'One more approval decides this.',
        blockedByReject:
          '{count, plural, one {# rejection recorded} other {# rejections recorded}} — this proposal can no longer pass.',
        blockedByAbstain:
          '{count, plural, one {# abstention recorded} other {# abstentions recorded}} — with a threshold of {threshold}, this proposal can no longer pass.',
      },
      closed: 'Voting has closed',
      closesInMinutes: 'closes in {count, plural, one {# minute} other {# minutes}}',
      closesInHours: 'closes in {count, plural, one {# hour} other {# hours}}',
      closesInDays: 'closes in {count, plural, one {# day} other {# days}}',
      closesOn: 'closes {date}',
      openedOn: 'opened {date}',
      decidedOn: 'decided {date}',
      proposedBy: 'by {name}',
      youVoted: 'you: {choice}',
      youHaveNotVoted: 'you: not voted',
      whatIsProposed: 'What is proposed',
      council: 'Council',
      discussion: 'Discussion',
      noVotesYet: 'No ballots have been cast yet.',
      noDiff: 'This proposal changes nothing.',
      partOfBatch: 'Part of a batch',
      batchExplain:
        'Submitted together, voted separately. A relation is auto-rejected if the person it depends on is rejected.',
      seatNo: 'seat {seat}',
      seatVacated: 'seat vacated, ballot no longer counted',
      yourVote: 'Your vote (seat {seat})',
      comment: 'Comment (optional)',
      canChangeVote: 'You can change your vote while voting is open.',
      oneVotePerSeat: 'One vote per seat. You can change it while voting is open.',
      confirmDeciding:
        'Your approval reaches the threshold — this will apply the change immediately. Continue?',
      votingClosed: 'Voting on this proposal has closed.',
      nonElderExplainer:
        'The council decides this proposal. {approve} of {seats} seats have approved. You will be notified when it is decided.',
      withdraw: 'Withdraw…',
      withdrawReason: 'Why are you withdrawing it? (optional)',
      withdrawExplain:
        'A proposal cannot be edited once open. Withdrawing and resubmitting is how you amend one.',
      withdrawConfirm: 'Withdraw this proposal',
      decision: {
        approved: 'Approved by the council.',
        rejected: 'Rejected by the council.',
        expired: 'Expired — the voting window passed without a decision.',
        withdrawn: 'Withdrawn by the proposer.',
        dependencyRejected:
          'Automatically rejected: the person this relation depends on was rejected.',
      },
      diff: {
        field: 'Field',
        from: 'From',
        to: 'To',
        value: 'Value',
        removed: '(not set)',
      },
      fields: {
        given_names: 'Given names',
        family_name: 'Family name',
        birth_family_name: 'Family name at birth',
        nickname: 'Nickname',
        gender: 'Gender',
        living_status: 'Living status',
        birth: 'Born',
        death: 'Died',
        birth_place: 'Place of birth',
        death_place: 'Place of death',
        occupation: 'Occupation',
        biography: 'Biography',
        source_note: 'Source',
        status: 'Status',
        retire_reason: 'Reason for retirement',
        kind: 'Kind',
        parent_role: 'Role',
        parent_basis: 'Basis',
        union_type: 'Union type',
        end_reason: 'How it ended',
        sibling_basis: 'Basis',
        started: 'Started',
        ended: 'Ended',
        note: 'Note',
      },
    },
    council: {
      title: 'Council',
      settingsSummary: '{size} seats, {threshold} to approve, {days}-day window',
      edit: 'Edit council settings',
      seat: 'Seat',
      holder: 'Holder',
      since: 'Since',
      vacatedAt: 'Vacated',
      actions: 'Actions',
      vacant: '— vacant —',
      grant: 'Grant…',
      grantConfirm: 'Grant seat',
      choosePerson: 'Choose a member…',
      vacate: 'Vacate',
      confirmVacate:
        'Vacate {name}’s seat? Their ballots on open proposals stop counting immediately, and every open proposal is re-tallied.',
      showHistory: 'Show seat history',
      hideHistory: 'Hide seat history',
      deadlocked:
        '{vacant, plural, one {# vacant seat} other {# vacant seats}}: with a threshold of {threshold} and {seats} seated, no proposal can currently pass. Fill the seats or lower the threshold.',
      impactUnanimity:
        'With {size} of {size}, a single rejection or abstention blocks any proposal.',
      impactMajority:
        'With {threshold} of {size}, a proposal passes once {threshold} seats approve, whatever the rest do.',
      impactClamped:
        'Only {seats} seats are filled, so the threshold currently clamps to {effective}.',
      impactRetally:
        'Changing these re-tallies every open proposal at once — some may be decided on the spot.',
    },
    notifications: {
      title: 'Notifications',
      empty: 'Nothing to catch up on.',
      unread: '{count, plural, one {# unread notification} other {# unread notifications}}',
      markRead: 'Mark read',
      markAllRead: 'Mark all {count} read',
      kinds: {
        proposal_opened: 'New proposal: {summary}',
        proposal_decided: '{status}: {summary}',
        vote_needed: 'Your ballot is still awaited: {summary}',
        seat_granted: 'You were granted council seat {seat}.',
        photo_tagged: 'You were tagged in a photo.',
      },
    },
    audit: {
      title: 'Audit log',
      explain:
        'Every governed action, append-only. Nothing here can be edited or deleted — by anyone, including administrators.',
      action: 'Action',
      allActions: 'All actions',
      entityType: 'Entity type',
      allEntities: 'All entities',
      entity: 'Entity',
      actor: 'Actor',
      anyActor: 'Anyone',
      systemOnly: 'System jobs only',
      system: 'System',
      from: 'From',
      to: 'To',
      when: 'When',
      empty: 'No entries match these filters.',
      count: '{count, plural, one {# entry} other {# entries}}',
      moreAvailable: 'More entries are available — narrow the filters to see them.',
      showDetail: 'detail',
      hideDetail: 'hide',
    },
    errors: {
      credentials_invalid: 'Email or password is incorrect.',
      account_suspended: 'This account is suspended. Please contact an administrator.',
      account_invited: 'Use your invitation link to set a password first.',
      rate_limited: 'Too many attempts. Please wait a moment and try again.',
      invite_invalid: 'This invitation is no longer valid.',
      reset_token_invalid: 'This reset link is no longer valid.',
      validation_failed: 'Please check the highlighted fields.',
      not_found: 'That record does not exist, or is no longer part of the live tree.',
      conflict: 'Something else changed this record first. Reload and try again.',
      duplicate_assertion: 'That exact assertion is already recorded.',
      redundant_sibling:
        'Both shared parents are already recorded, so this siblinghood is derived — no explicit row is needed.',
      self_relation: 'A person cannot be related to themselves.',
      not_retirable: 'That record can no longer be changed.',
      not_implemented: 'That part of FamTree is not built yet.',
      proposal_not_open: 'This proposal has already been decided.',
      proposer_not_validated:
        'Your account must be linked to a validated person in the tree before you can propose changes.',
      stale_settings: 'The settings changed while you were editing. Reload and try again.',
      invalid_cursor: 'That page of results has expired. Start the search again.',
      forbidden: 'You do not have access to that.',
      unauthenticated: 'Please sign in.',
      offline: 'You appear to be offline.',
      generic: 'Something went wrong. Please try again.',
    },
    photos: {
      title: 'Photos',
      uploadTitle: 'Add photos',
      addPhotos: 'Add photos',
      hideUploader: 'Done adding',
      dropHere: 'Drop photos here',
      browse: 'Choose files…',
      limits: 'JPEG, PNG or WebP, up to 25 MB each. Three upload at a time.',
      applyToAll: 'Apply to every photo in this batch',
      applyToAllHelp:
        'Forty photos from the same wedding share a date and the same faces. Set them once here; you can correct any individual photo afterwards.',
      taken: 'When it was taken',
      caption: 'Caption',
      people: 'Who is in it',
      addPerson: 'Tag someone',
      removeTag: 'Remove',
      primary: 'main subject',
      details: 'Details',
      save: 'Save',
      close: 'Close',
      view: 'View',
      untitled: 'Untitled photo',
      loadingName: '…',
      noSubjects: 'Nobody is tagged yet — do you recognise anyone?',
      uploading: 'Uploading…',
      emptyGallery: 'No photos of this person yet.',
      undatedShelf:
        '{count, plural, one {# undated photo — can you place it in time?} other {# undated photos — can you place them in time?}}',
      moreAvailable: 'More photos are available.',
      usePortrait: 'Use as {name}’s portrait',
      delete: 'Delete photo',
      deleteHelp:
        'Deleting hides the photo straight away; the file is kept for 90 days in case it was a mistake.',
      states: {
        queued: 'Waiting',
        uploading: 'Uploading',
        done: 'Added',
        duplicate: 'Already in the album',
        failed: 'Failed',
      },
    },
    claim: {
      title: 'Are you in the tree?',
      help: 'Most people in a family tree never have an account — but every account is someone in it. If a relative has already recorded you, say so and an administrator will confirm it. Until then you can read the tree but not propose changes.',
      whoAreYou: 'Which of these is you',
      submit: 'That is me',
      notThere:
        'Not in the tree yet? Ask a relative who is to add you — then come back and claim it.',
      waiting:
        'You have asked to be linked to someone in the tree. An administrator will confirm it; you will be told either way.',
      withdraw: 'Withdraw that',
      adminTitle: 'Identity claims',
      adminHelp:
        'Someone is asking to be a person in the tree. Approving hands them that person’s place in the family and the right to propose changes, so confirm it the way you would in person.',
      note: 'Note (optional, kept in the audit log)',
      approve: 'Confirm — this is them',
      refuse: 'Not them',
    },
    lineage: {
      title: 'Find someone by descent',
      help: 'In a family that reuses a grandfather’s name, “Hassan” finds nine people and identifies none of them. Name the line instead and the tree will walk it.',
      label: 'Name, then their parent, then theirs…',
      placeholder: 'Hassan ben Idriss ben Mehdi',
      submit: 'Search the line',
      sideHint:
        'ben · bin · ibn · ould · bent · bint · > all mean “child of”. Put f: or m: before a name to say it must be the father or the mother — useful when both are recorded, or when it is the mother’s line you have.',
      readAs: 'Read as: {chain}',
      matchCount:
        '{count, plural, one {# person matches this line} other {# people match this line}}',
      noMatch:
        'Nobody in the tree matches that line. Check a spelling, or drop the last link and see who comes back.',
      truncated: 'More matched than are shown — add another link to narrow it.',
      partial: 'partial name match',
      sides: { father: 'father', mother: 'mother', any: 'either parent' },
      via: {
        father: 'son/daughter of his father',
        mother: 'son/daughter of his mother',
        parent: 'child of',
      },
    },
    tree: {
      title: 'Family tree',
      chartLabel: 'Family tree chart. Use the arrow keys to move between people.',
      view: 'View',
      views: { family: 'Household', ancestors: 'Ancestors', descendants: 'Descendants' },
      generations: 'Generations',
      showPending: 'Show proposed changes',
      trail: 'Where you have been',
      print: 'Print',
      pickSomeone: 'Choose a person to draw the tree around.',
      nothingToDraw: 'Nothing to draw yet — this person has no recorded relations.',
      hint: 'Drag to move, scroll to zoom. Arrow keys walk between people; Enter opens a profile; double-click re-centres the tree.',
      bands: {
        focus: 'This generation',
        parents: 'Parents',
        grandparents: 'Grandparents',
        children: 'Children',
        grandchildren: 'Grandchildren',
        ancestors: 'Earlier',
        descendants: 'Later',
      },
      unionState: {
        ongoing: 'Together',
        divorce: 'Ended by divorce',
        annulment: 'Annulled',
        separation: 'Separated',
        widowhood: 'Ended by death',
        dissolved: 'Dissolved',
        unknown: 'Ended, reason unrecorded',
      },
      legend: {
        biological: 'Parent',
        adoptive: 'Adoptive',
        foster: 'Foster',
        guardianship: 'Guardianship',
        endedUnion: 'Union that has ended',
        stepNote:
          'Step-parents are not drawn: the chart shows the marriage and the other parent, and the relationship follows from those.',
      },
    },
    relativeDate: {
      toggle: 'Or say when, relative to something else',
      hide: 'Enter a date instead',
      help: 'Families remember order, not dates. If you know this happened around the time of something else, say so here and the tree will work out roughly when — keeping your words exactly as you said them.',
      anchorPerson: 'Relative to whom',
      event: 'Which event',
      events: { birth: 'their birth', death: 'their death' },
      relation: 'When, in relation to it',
      relations: {
        same_year: 'the same year',
        same_month: 'the same month',
        years_after: 'a number of years after',
        years_before: 'a number of years before',
        just_after: 'just after',
        just_before: 'just before',
      },
      years: 'How many years',
      anchorEvent: { birth: '{name} was born', death: '{name} died' },
      reads: 'Recorded as: “{wording}”',
      stores: 'Which places it at {date}.',
      storesWordingOnly:
        'That person’s own date is not recorded, so only your wording is kept — no year can be worked out from it yet.',
      notLive:
        'This is worked out once, now. If the other date is corrected later, this one will not move on its own — but your wording stays, so it can be redone.',
      apply: 'Use this date',
    },
    kinship: {
      title: 'How are two people related?',
      intro:
        'Pick two people. Every answer is a live computation over the recorded assertions — nothing here is stored, so it corrects itself the moment a parent or a marriage is added.',
      from: 'First person',
      to: 'Second person',
      swap: 'Swap',
      useMe: 'Use me',
      find: 'Find paths',
      closest: 'Closest relation',
      alsoRelated: 'Also related as…',
      samePerson: 'That is the same person on both sides.',
      noRelation:
        'No recorded relationship between them. They may still be connected further out than the search reaches.',
      relatedWays: '{count, plural, one {Related in # way} other {Related in # distinct ways}}',
      steps: '{count, plural, one {# step} other {# steps}}',
      consanguineous:
        'These two are related by blood as well as by marriage — both answers are shown.',
      datesUnknown: 'Some dates are unrecorded, so the timing behind this answer is unproven.',
      truncated: 'The search hit its limits; there may be further connections.',
      copyLink: 'Copy link',
      copied: 'Link copied',
      panelTitle: 'Derived relations',
      panelNote:
        'Computed from the recorded parent, union and sibling facts — never stored, and never edited directly. To change one of these, change the facts underneath it.',
      panelEmpty:
        'Nothing is derivable yet: this person has no recorded relations to compute from.',
      panelTruncated: 'Showing the closest relations; there are more further out.',
      showAll: 'Show all',
      showFewer: 'Show fewer',
      via: 'via {names}',
      computed: 'Computed',
      groups: {
        parent: 'Parents',
        child: 'Children',
        grandparent: 'Grandparents',
        grandchild: 'Grandchildren',
        great_grandparent: 'Great-grandparents',
        great_grandchild: 'Great-grandchildren',
        sibling: 'Siblings',
        half_sibling: 'Half-siblings',
        step_sibling: 'Step-siblings',
        spouse: 'Spouses',
        co_spouse: 'Co-spouses',
        pibling: 'Aunts and uncles',
        nibling: 'Nieces and nephews',
        cousin: 'Cousins',
        parent_in_law: 'Parents-in-law',
        child_in_law: 'Children-in-law',
        sibling_in_law: 'Siblings-in-law',
        step_parent: 'Step-parents',
        step_child: 'Step-children',
        guardian: 'Guardians',
        ward: 'Wards',
        other: 'Other relations',
      },
      annotations: {
        adoptive: 'adoptive',
        foster: 'foster',
        guardianship: 'through guardianship',
        by_marriage: 'by marriage',
        former: 'former',
        unknown_basis: 'basis unrecorded',
      },
      edges: { U: 'child of', D: 'parent of', M: 'married to', S: 'sibling of' },
      recordTitle: 'Record how they are related',
      recordHelp:
        'The tree stores parents, partners and siblings; everything else — cousins, aunts, in-laws — is worked out from those. Tell it how these two are related and it will add the people in between, unnamed where nobody knows the name.',
      recordClaim: '{name} is their…',
      recordSubmit: 'Record this',
      answerFirst: 'Answer the question above first.',
      planTitle: 'What this will add',
      planSummary:
        '{people, plural, =0 {No new people} one {# new person} other {# new people}}, {links, plural, one {# parent link} other {# parent links}}.',
      willCreate: 'a person nobody has named yet',
      alreadyRecorded: 'already recorded',
      orChoose: 'or choose someone already in the tree',
      whichLine: 'Which of {name}’s parents is this through?',
      rung: { from: 'On the first person’s side:', to: 'On the second person’s side:' },
      claimSource: 'Recorded from the statement that {from} and {to} are {claim}.',
      claimRecorded:
        '{count, plural, =0 {Recorded — nothing new was needed.} one {Recorded, adding # unnamed person.} other {Recorded, adding # unnamed people.}}',
      claims: {
        sibling: 'sibling',
        half_sibling: 'half-sibling',
        grandparent: 'grandparent',
        grandchild: 'grandchild',
        pibling: 'aunt or uncle',
        nibling: 'niece or nephew',
        first_cousin: 'first cousin',
        first_cousin_once_removed: 'first cousin once removed',
        second_cousin: 'second cousin',
      },
    },
    notFound: {
      title: 'Page not found',
      body: 'That address does not exist in FamTree.',
      home: 'Go home',
    },
  },
} as const;
